import React, { useEffect } from 'react'
import { isPresent } from 'ts-extras'
import { Actions } from '~/actions'
import { Collapsible } from '~/components/Collapsible'
import { Checkmark } from '~/components/Icons'
import type { HTMLElementProps, InTheBoxBlock, Product, InTheBoxSection as SectionType } from '~/types'
import { classnames, cn, imageUrl, useDispatch, useSelector } from '~/utils'
import { attributesToProps, Section } from './Common'

interface Props {
	section: SectionType
}

const BoxTitle: React.FC<{ title: string; quantity?: number }> = ({ title, quantity }) => (
	<h3 className="my-0 inline-block text-2xl text-green-700">
		{quantity ? (
			<span>
				{quantity} x <span dangerouslySetInnerHTML={{ __html: title }} />
			</span>
		) : (
			<span dangerouslySetInnerHTML={{ __html: title }} />
		)}
	</h3>
)

interface BoxItemProps extends HTMLElementProps<'li'> {
	item: InTheBoxBlock['settings']
	inCart: boolean
	product: Product | undefined
	settings: SectionType['settings']
}

const BoxItem: React.FC<BoxItemProps> = ({ item, product, inCart }) => {
	const title = item.name || product?.title || ''
	return (
		<li className={cn('space-y-4', { 'opacity-30': !inCart })}>
			<div className="flex justify-center">
				{item.description ? (
					<Collapsible title={<BoxTitle title={title} quantity={item.quantity} />}>
						<p className="text-xl" dangerouslySetInnerHTML={{ __html: item.description }} />
					</Collapsible>
				) : (
					<BoxTitle title={title} quantity={item.quantity} />
				)}
			</div>
			<div className="relative overflow-hidden rounded-3xl bg-stone-100">
				<img
					className="xs:h-52 block h-44 w-full object-cover lg:h-64"
					src={imageUrl(item.image || product?.featuredImage, 400)}
					loading="lazy"
				/>
				{inCart && (
					<div className="absolute left-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-green-700 text-sm">
						<Checkmark className="mt-0.5" />
					</div>
				)}
			</div>
		</li>
	)
}

export const InTheBoxSection: React.FC<Props> = ({ section }) => {
	const lineItems = useSelector(s => s.cart.lines)?.map(item => item?.merchandise?.product?.handle)
	const stagedLineItems = useSelector(s => s.cart.stagedUpsells)?.map(item => item.product?.handle)
	const handlesInCart = [lineItems, stagedLineItems].flat().filter(isPresent)
	const products = useSelector(s => s.products.products)

	const dispatch = useDispatch()
	useEffect(() => {
		if (!products) dispatch(Actions.loadProducts())
	}, [products, dispatch])

	return (
		<Section
			tag={section.tag}
			className={classnames('space-y-9 text-center md:space-y-16', {})}
			style={{
				backgroundColor: section.settings.background_color,
				color: section.settings.color,
				paddingTop: `${section.settings.size_top}rem`,
				paddingBottom: `${section.settings.size_bottom}rem`
			}}
		>
			<h2 className="text-5xl">{section.settings.title}</h2>
			<ul
				className={classnames('list grid grid-cols-2 grid-rows-[auto,auto] gap-8 sm:grid-cols-3', {
					'lg:grid-cols-4': (section.blocks?.length ?? 0) > 6
				})}
			>
				{section.blocks?.map(({ id, settings: item, attributes }) => {
					const product = products?.find(p => p.handle === item.product)
					const inCart = isInCart(handlesInCart, item)
					if (section.settings.only_added && !inCart) return null
					return (
						<BoxItem
							key={id}
							item={item}
							product={product}
							inCart={inCart}
							{...attributesToProps(attributes)}
						/>
					)
				})}
			</ul>
			{section.settings.summary_image && (
				<img
					className="block w-full rounded-3xl object-cover sm:hidden"
					src={imageUrl(section.settings.summary_image)}
					loading="lazy"
				/>
			)}
		</Section>
	)
}

function isInCart(handles: string[], item: InTheBoxBlock['settings']) {
	return window.PRODUCT?.handle === item.product || handles.includes(item.product)
}
